"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { platformFetch } from "@/lib/api/browser";

/* The platform's own realtime stream, alongside Ably.

   Ably carries chat. This carries what the platform itself publishes:
   transactions and account notifications. They are separate on purpose — chat
   is ours, these events are the platform's, and neither should be able to take
   the other down.

   Two properties of that stream shape everything here, both from its own
   contract: the token expires with the session and a stale one goes quiet
   without closing the socket, so a fresh URL is fetched for every attempt
   including reconnects; and there is no replay, so an event is a prompt to
   refetch rather than a source of truth. */

export type PlatformEventStatus = "connecting" | "live" | "offline";

export type PlatformEvent = {
  id: string;
  topic: string;
  name: string;
  kind?: string;
  status?: string;
  type?: string;
  at: string;
};

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const EVENT_CAP = 8;
/* The client keeps the connection alive, not the server: the gateway closes
   a socket idle for 10 minutes, so a ping goes out comfortably inside that. */
const PING_INTERVAL_MS = 5 * 60 * 1000;
const SEEN_CAP = 64;

type Packet = { id: string; name: string; payload?: unknown };

/* One delivered message, deduplicated: the topic it came on, the package name
   (`transaction`, `account-notification`, …) and its data, unparsed. */
export type PlatformMessage = { topic: string; name: string; data: unknown };

export function usePlatformEvents({
  accountId,
  onEvent,
  onMessage,
  enabled = true,
}: {
  accountId: string | null;
  onEvent?: () => void;
  onMessage?: (message: PlatformMessage) => void;
  enabled?: boolean;
}) {
  const [status, setStatus] = useState<PlatformEventStatus>("connecting");
  const [events, setEvents] = useState<PlatformEvent[]>([]);

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const client = useQueryClient();

  /* One place to mark every money query stale: fired for money events, and
     on every (re)connect — the stream has no replay, so whatever happened
     while disconnected is only found by asking the REST API again. */
  const refetchMoney = useCallback(() => {
    client.invalidateQueries({ queryKey: ["balances"] });
    client.invalidateQueries({ queryKey: ["account", "transfers"] });
    client.invalidateQueries({ queryKey: ["stablemint-transactions"] });
    client.invalidateQueries({ queryKey: ["incentives", "rewards"] });
    client.invalidateQueries({ queryKey: ["incentives", "rollups"] });
  }, [client]);

  /* Delivery is at-least-once and multi-stage: the same event id can arrive
     twice, and a transaction sends pending then success/error under one id.
     An exact repeat is dropped; a new stage supersedes the entry it had. */
  const seenRef = useRef(new Map<string, string>());

  const record = useCallback(
    (packet: Packet) => {
      const payload = (packet.payload ?? {}) as {
        id?: string;
        topic?: string;
        name?: string;
        data?: { kind?: string; status?: string; type?: string; data?: unknown };
      };

      const eventId = payload.id ?? packet.id;
      const stage = `${payload.name ?? ""}|${payload.data?.status ?? ""}|${payload.data?.type ?? ""}`;
      const seen = seenRef.current;
      if (seen.get(eventId) === stage) return;
      seen.set(eventId, stage);
      if (seen.size > SEEN_CAP) {
        const oldest = seen.keys().next().value;
        if (oldest !== undefined) seen.delete(oldest);
      }

      setEvents((current) =>
        [
          {
            id: eventId,
            topic: payload.topic ?? "unknown",
            name: payload.name ?? "event",
            kind: payload.data?.kind,
            status: payload.data?.status,
            type: payload.data?.type,
            at: new Date().toISOString(),
          },
          ...current.filter((entry) => entry.id !== eventId),
        ].slice(0, EVENT_CAP),
      );

      /* A transaction event is the platform saying money moved. Since there is
         no replay, the event is a prompt to refetch rather than a value to
         trust — so the money queries are marked stale and read again.

         Without this a confirmed deposit shows nothing until `useBalances`
         polls, up to 30 seconds later, and the socket that already knew sits
         unheard. Only the account's own topics move this player's money: the
         public `transactions` feed fires for every bet on the site, and now
         that the connection is held app-wide (PlatformEventsProvider) it must
         not refetch the balance each time. */
      const topic = payload.topic ?? "";
      if (topic.startsWith("accounts:") && (topic.endsWith(":transactions") || topic.endsWith(":notifications"))) {
        refetchMoney();
      }

      onMessageRef.current?.({ topic, name: payload.name ?? "event", data: payload.data });
      onEventRef.current?.();
    },
    [refetchMoney],
  );

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | undefined;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pinger: ReturnType<typeof setInterval> | undefined;
    let disposed = false;
    const pendingSubscribes = new Map<string, string>();

    const connect = async () => {
      if (disposed) return;
      setStatus((current) => (current === "live" ? "connecting" : current));

      const url = (
        await platformFetch<{ data: { url: string } }>("realtime").catch(() => null)
      )?.data?.url;

      if (!url) {
        schedule();
        return;
      }

      if (disposed) return;

      socket = new WebSocket(url);

      socket.onopen = () => {
        const wasReconnect = retry > 0;
        retry = 0;
        setStatus("live");

        const topics = ["transactions"];
        if (accountId) {
          topics.push(`accounts:${accountId}:transactions`, `accounts:${accountId}:notifications`);
        }

        for (const topic of topics) {
          const id = crypto.randomUUID();
          pendingSubscribes.set(id, topic);
          socket?.send(JSON.stringify({ id, name: "subscribe", payload: { topic } }));
        }

        /* The client owns keepalive: the gateway closes an idle socket at
           ten minutes, and the server never pings first. */
        pinger = setInterval(() => {
          socket?.send(JSON.stringify({ id: crypto.randomUUID(), name: "ping" }));
        }, PING_INTERVAL_MS);

        if (wasReconnect) refetchMoney();
      };

      socket.onmessage = (event) => {
        let packet: Packet;

        try {
          packet = JSON.parse(String(event.data)) as Packet;
        } catch {
          return;
        }

        /* pong answers our keepalive and carries nothing. */
        if (packet.name === "pong") return;

        if (packet.name === "subscribe-ack") {
          const topic = pendingSubscribes.get(packet.id);
          pendingSubscribes.delete(packet.id);
          const ok = (packet.payload as { success?: boolean } | undefined)?.success === true;
          if (!ok) {
            /* Terminal per the contract: retrying the identical packet gives
               the identical answer, so it is surfaced rather than retried. */
            console.warn(`platform realtime: subscription rejected for ${topic ?? packet.id}`);
          }
          return;
        }

        if (packet.name === "message") record(packet);
      };

      socket.onclose = () => {
        if (pinger) clearInterval(pinger);
        pinger = undefined;
        pendingSubscribes.clear();
        if (!disposed) schedule();
      };

      socket.onerror = () => socket?.close();
    };

    const schedule = () => {
      if (disposed) return;
      setStatus("offline");

      const wait = Math.min(RECONNECT_BASE_MS * 2 ** retry, RECONNECT_MAX_MS);
      retry += 1;
      timer = setTimeout(connect, wait + Math.random() * 500);
    };

    void connect();

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (pinger) clearInterval(pinger);
      socket?.close();
    };
  }, [accountId, enabled, record, refetchMoney]);

  return { status, events };
}
